/**
 * #105 Phase 3: NIST randomness beacon anchoring. Polls
 * `beacon.nist.gov`'s public pulse (no auth, no user content involved — this
 * only ever reads an already-public value) and relays it into the Rust-side
 * cache `chronicle_after_commit` reads from. Rust never makes this network
 * call itself; see `beacon_cache.rs` for why.
 */
import { invoke } from "@tauri-apps/api/core";
import { isTauri } from "./runtime";

const BEACON_PULSE_URL = "https://beacon.nist.gov/beacon/2.0/pulse/last";
/** Matches the beacon's own ~60s pulse period — no point polling faster
 * than the value can actually change. */
const REFRESH_INTERVAL_MS = 60_000;

async function fetchAndRecordPulse(): Promise<void> {
  try {
    const response = await fetch(BEACON_PULSE_URL);
    if (response.ok === false) return;
    const body = await response.json();
    const index = body?.pulse?.pulseIndex;
    const value = body?.pulse?.outputValue;
    if (typeof index !== "number" || typeof value !== "string") return;
    await invoke("record_beacon_pulse", { index, value });
  } catch {
    // Best-effort and offline-safe: leave whatever pulse Rust already has
    // cached (from an earlier successful fetch) in place.
  }
}

/**
 * Starts polling the beacon and relaying each pulse into Rust. Never
 * awaited by anything on the save path — a slow or failed fetch here just
 * means the next chronicle entry goes out unanchored, not that a save
 * waits on it. No-op outside Tauri (nothing consumes the cache there).
 */
export function installBeaconPolling(): () => void {
  if (isTauri() === false) return () => {};
  void fetchAndRecordPulse();
  const timer = setInterval(
    () => void fetchAndRecordPulse(),
    REFRESH_INTERVAL_MS,
  );
  return () => clearInterval(timer);
}
